import React, { useState, useEffect } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import { Container, Box } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import { listen } from "Services/Listen";
import ListeningStatus from "Components/Global/ListeningStatus";
import useVersion from "../shared/useVersion";
import "./GiveAndTake.scss";
import icon from "./giveandtake-icon.png";
import { getUserData } from "Services/API";
import { ipaToAmirabet } from "../../../app/services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

const colors = {
  yellow: "#FFDC00",
  green: "#2ECC40",
};

export default function GiveAndTakeValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.give_and_take;

  const { id } = useVersion(props.wordMetadata, {
    1: ["PHON", "GRAPHEMIC_BREAKDOWN"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: GiveAndTake,
  }[versionId];

  return <Version {...props} close={close} versionId={versionId} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.speech.stopListening();
    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function GiveAndTake(props) {
  const { word, wordMetadata, speech, ftue } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN, SUB, SUB_PHON, SUB_POSITION } =
    wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(0);
  const [highlight, setHighlight] = useState(null);
  const [showGraphemes, setShowGraphemes] = useState(false);
  const [graphemeIndex, setGraphemeIndex] = useState(-1);
  const [colorIndex, setColorIndex] = useState(0);
  const [showSub, setShowSub] = useState(false);
  const [highlightSub, setHighlightSub] = useState(false);

  const isStart = SUB_POSITION === "start";
  const targetIndex = isStart ? 0 : graphemes.length - 1;

  const { speak, speakFile, speakPhonemes, listenWord, listenPhoneme, speakSequence } =
    useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function sayWordPart() {
    if (!props.isMountedRef.current) return;

    try {
      await cancellablePromise(sleep());
      for (let i = 0; i < files.length; i++) {
        if (i === targetIndex) continue;
        setColorIndex((i) => i + 1);
        await cancellablePromise(speakFile(files[i]));
      }
      setColorIndex(0);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0: {
          const position = isStart ? "last" : "first";
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.time_to_get_goofy,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.watch_and_listen(isStart),
            ]));
          } else {
            await cancellablePromise(speak(`${ttsStrings.time_to_get_goofy} "${word}". ${ttsStrings.watch_and_listen(isStart)}`));
          }
          await cancellablePromise(sayWordPart());
          await cancellablePromise(speak(ttsStrings.describe({ nSounds: graphemes.length - 1 })));
          await cancellablePromise(sleep());
          await cancellablePromise(speak(ttsStrings.ask_word_part));
          setHighlight(colors.yellow);
          await cancellablePromise(sayWordPart());
          const { status } = await cancellablePromise(listenWord({
            targetWord: "",
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          if (status === "match") await speak(ttsStrings.correct_1);
          else {
            // struggle or silence
            await cancellablePromise(speak(ttsStrings.struggle_1));
            await cancellablePromise(sayWordPart());
          }
          await cancellablePromise(sleep());
          setHighlight(null);
          setStage(1);
          break;
        }
        case 1: {
          await cancellablePromise(speak(ttsStrings.add_sound({ isStart })));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speakFile(files[targetIndex]));
          if (!ftue) {
            await cancellablePromise(sleep());
            setStage(2);
            break;
          }
          await cancellablePromise(speak(ttsStrings.word_is));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speakPhonemes({ phon: PHON }));
          await cancellablePromise(speak(ttsStrings.makes_word));
          setHighlight(colors.green);
          await cancellablePromise(speak(`"${word}".`));
          await cancellablePromise(setShowGraphemes(true));
          await cancellablePromise(sleep(3000));
          setShowGraphemes(false);
          setHighlight(null);
          setStage(2);
          break;
        }
        case 2: {
          // phoneme listening stage
          await cancellablePromise(speak(ttsStrings.ask_sounds({ nSounds: graphemes.length })));
          for (let i = 0; i < graphemes.length; i++) {
            setGraphemeIndex(i);
            const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] }));
            if (status === "skip") return props.close();
          }
          setGraphemeIndex((i) => i + 1);
          await cancellablePromise(speak(ttsStrings.correct_2));
          await cancellablePromise(sleep());
          setStage(3);
          break;
        }
        case 3: {
          await cancellablePromise(speak(ttsStrings.ask_word));
          setHighlight(colors.yellow);
          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          if (status === "match") {
            setHighlight(colors.green);
            await cancellablePromise(speak(ttsStrings.correct_3));
          } else {
            // struggle or silence
            setShowGraphemes(true);
            await cancellablePromise(speak(ttsStrings.struggle_2));
            const { status: status2 } = await cancellablePromise(listenWord({
              targetWord: word,
              patterOverride: ["match", "struggle", "silence"],
            }));
            if (status2 === "skip") return props.close();
            if (status2 === "match") await cancellablePromise(speak(ttsStrings.correct_3));
            else await cancellablePromise(speak(`"${word}".`));
          }
          if (!SUB) {
            await cancellablePromise(speak(ttsStrings.good_job));
            return props.close();
          }
          await cancellablePromise(sleep());
          setHighlight(null);
          setStage(4);
          break;
        }
        case 4: {
          setShowGraphemes(true);
          const soundOut = files[targetIndex];
          const subFiles = getAudioForPhonemes(SUB_PHON, getUserData().locale);
          const subIndex = isStart ? 0 : subFiles.length - 1;
          const soundIn = subFiles[subIndex];
          await cancellablePromise(speak(ttsStrings.replace_sound));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speakFile(soundOut));
          await cancellablePromise(speak(ttsStrings.to));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speakFile(soundIn));

          // show sub transition
          setShowSub(true);
          setHighlightSub(true);
          await cancellablePromise(sleep());
          setHighlightSub(false);
          await cancellablePromise(sleep(2000));

          setHighlight(colors.yellow);
          setShowGraphemes(true);
          await cancellablePromise(speak(ttsStrings.ask_sub));
          const { status } = await cancellablePromise(listenWord({
            targetWord: "",
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          if (status === "match"){
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.ok_new_word_is,
                {textToSpeak: SUB, skipTranslation: true},
                ttsStrings.thanks,
              ]));
            } else {
              await cancellablePromise(speak(`${ttsStrings.ok_new_word_is} "${SUB}". ${ttsStrings.thanks}`));
            }
          } else{
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.this_word_is,
                {textToSpeak: SUB, skipTranslation: true},
                ttsStrings.back_to_reading,
              ]));
            } else {
              await cancellablePromise(speak(`${ttsStrings.this_word_is} "${SUB}". ${ttsStrings.back_to_reading}`));
            }
          }
          props.close();
          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      <div style={{ marginTop: 10 }}>
        <ListeningStatus />
      </div>
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      return (
        <div className="gt-boxes">
          {graphemes.map((grapheme, i) => {
            if (i === 0) return; // render n-1 boxes
            const boxStyle = { margin: 0 };
            // collapse borders of adjacent boxes
            if (i < graphemes.length - 1) boxStyle.borderRight = 0;
            if (highlight) boxStyle.background = highlight;
            if (colorIndex >= i) boxStyle.background = colors.green;
            return <Box key={`0/${i}`} className="gt-box" style={boxStyle} />;
          })}
        </div>
      );
    }

    if (stage === 1) {
      return (
        <div className="gt-boxes">
          {graphemes.map((grapheme, i) => {
            const boxStyle = { margin: 0 };
            if (i === targetIndex) {
              boxStyle.background = colors.green;
              boxStyle.margin = "0 40px";
            } else {
              // collapse borders of adjacent boxes
              if (targetIndex === 0) {
                if (i < graphemes.length - 1) boxStyle.borderRight = 0;
              } else if (i < graphemes.length - 2) boxStyle.borderRight = 0;
            }

            if (highlight) {
              boxStyle.margin = 0;
              boxStyle.background = highlight;
              if (i < graphemes.length - 1) boxStyle.borderRight = 0;
            }

            return (
              <Box key={`1/${i}`} className="gt-box" style={boxStyle}>
                {showGraphemes && <div className="gt-letter">{grapheme}</div>}
              </Box>
            );
          })}
        </div>
      );
    }

    if (stage === 2) {
      return (
        <div className="gt-boxes">
          {graphemes.map((grapheme, i) => {
            const boxStyle = { background: colors.yellow };
            if (graphemeIndex > i) boxStyle.background = colors.green;
            return <Box key={`2/${i}`} className="gt-box" style={boxStyle} />;
          })}
        </div>
      );
    }

    if (stage === 3) {
      return (
        <div className="gt-boxes">
          {graphemes.map((grapheme, i) => {
            const boxStyle = { margin: 0 };
            // collapse borders of adjacent boxes
            if (i < graphemes.length - 1) boxStyle.borderRight = 0;
            if (highlight) boxStyle.background = highlight;
            return (
              <Box key={`3/${i}`} className="gt-box" style={boxStyle}>
                {showGraphemes && <div className="gt-letter">{grapheme}</div>}
              </Box>
            );
          })}
        </div>
      );
    }

    if (stage === 4) {
      return (
        <div className="gt-boxes">
          {graphemes.map((grapheme, i) => {
            const boxStyle = { margin: 0 };

            // replace first or last grapheme
            let content = grapheme;
            if (i === targetIndex && showSub) {
              const letterIndex = isStart ? 0 : SUB.length - 1;
              content = SUB[letterIndex].toLowerCase();
              if (highlightSub) boxStyle.background = colors.green;
            }
            // collapse borders of adjacent boxes
            if (i < graphemes.length - 1) boxStyle.borderRight = 0;
            if (highlight) boxStyle.background = highlight;
            return (
              <Box key={`3/${i}`} className="gt-box" style={boxStyle}>
                {showGraphemes && <div className="gt-letter">{content}</div>}
              </Box>
            );
          })}
        </div>
      );
    }
  }
}
